import config, { Environment } from 'config'
import {
  UserCredential,
  applyActionCode,
  indexedDBLocalPersistence,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  updateEmail,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { Metadata } from 'grpc-web'
import Vue from 'vue'
import firebaseApp from '~/firebase/defaultApp'
import { getHostForSiteConfig, trimHostForBranchDeployOnDev } from '~/utils/env'

declare module 'vue/types/vue' {
  interface Vue {
    $signInWithEmailLink(email: string, url: string): Promise<UserCredential>
    $signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential>
    $signInWithCustomToken(customToken: string): Promise<UserCredential>
    $signOut(): Promise<void>
    $isSignInWithEmailLink(url: string): boolean
    $sendPasswordResetEmail(email: string): Promise<void>
    $verifyPasswordResetCode(actionCode: string): Promise<string>
    $confirmPasswordReset(actionCode: string, newPassword: string): Promise<void>
    $updateEmail(email: string): Promise<void>
    $handleRecoverEmail(actionCode: string): Promise<string>
    $getMetadata(): Promise<Metadata>
  }
}

Vue.prototype.$signInWithEmailLink = async (email: string, url: string) => {
  await getAuth(firebaseApp).setPersistence(indexedDBLocalPersistence)
  return signInWithEmailLink(getAuth(firebaseApp), email, url)
}

Vue.prototype.$signInWithEmailAndPassword = async (email: string, password: string) => {
  await getAuth(firebaseApp).setPersistence(indexedDBLocalPersistence)
  return signInWithEmailAndPassword(getAuth(firebaseApp), email, password)
}

Vue.prototype.$signInWithCustomToken = (customToken: string) => {
  return signInWithCustomToken(getAuth(firebaseApp), customToken)
}

Vue.prototype.$signOut = async () => {
  await getAuth(firebaseApp).signOut()
}

Vue.prototype.$isSignInWithEmailLink = (url: string) => {
  return isSignInWithEmailLink(getAuth(firebaseApp), url)
}

Vue.prototype.$sendPasswordResetEmail = (email: string) => {
  const trimmedHost = trimHostForBranchDeployOnDev(location.host)
  return sendPasswordResetEmail(getAuth(firebaseApp), email, {
    url: `https://${trimmedHost}/newPassword`,
  })
}

Vue.prototype.$verifyPasswordResetCode = (actionCode: string) => {
  return verifyPasswordResetCode(getAuth(firebaseApp), actionCode)
}

Vue.prototype.$confirmPasswordReset = (actionCode: string, newPassword: string) => {
  return confirmPasswordReset(getAuth(firebaseApp), actionCode, newPassword)
}

Vue.prototype.$updateEmail = (email: string) => {
  const currentUser = getAuth(firebaseApp).currentUser
  if (currentUser === null) {
    throw new Error('Sign in required')
  }

  return updateEmail(currentUser, email)
}

Vue.prototype.$handleRecoverEmail = async (actionCode: string) => {
  const currentUser = getAuth(firebaseApp).currentUser
  if (currentUser === null) {
    throw new Error('Sign in required')
  }
  let restoredEmail: string | null | undefined = null
  const info = await checkActionCode(getAuth(firebaseApp), actionCode)
  restoredEmail = info.data.email
  await applyActionCode(getAuth(firebaseApp), actionCode)
  return restoredEmail
}

Vue.prototype.$getMetadata = async () => {
  const pathName = location.pathname
  const useLocalhost = config.apiBaseUrl.includes('localhost')

  // ローカルからの接続時はIPアドレス制限をスキップする
  const shouldRestrictIPAddress = config.environment !== Environment.local

  if (useLocalhost) {
    const uid = getAuth(firebaseApp).currentUser?.uid
    const base64Json = btoa(JSON.stringify({ user_id: uid }))
    const host = getHostForSiteConfig(location.host)
    const projectId = config.siteConfigMapping[host].firebaseConfig.projectId
    return {
      'x-endpoint-api-userinfo': base64Json,
      'x-stailer-external-admin-web': shouldRestrictIPAddress ? '1' : undefined,
      'x-forwarded-for': '150.249.249.44', // ローカルなので偽装
      'x-stailer-project-id': projectId,
      'x-stailer-rpc-caller-web-path': pathName,
    }
  } else {
    const token = await getAuth(firebaseApp).currentUser?.getIdToken()
    return {
      Authorization: `Bearer ${token}`,
      'x-stailer-external-admin-web': shouldRestrictIPAddress ? '1' : undefined,
      'x-stailer-rpc-caller-web-path': pathName,
    }
  }
}
